import React from 'react';
import { Bar } from 'react-chartjs-2';

import { Row, Col, DatePicker, Button, Select, Table } from 'antd';
import { DownloadOutlined, BarChartOutlined, LineChartOutlined } from '@ant-design/icons';
import SpanComponent from '@sharedComponent/span-component';
import { FullscreenOutlined, FullscreenExitOutlined } from '@ant-design/icons';
import AppLoader from '@sharedComponent/loaders/app-loader';
import Header from '@sharedComponent/header/header';
import { PerformanceExcelDownload } from '@sharedComponent/excel/excel';
import moment from 'moment';

import { performanceReportColoums } from '@constants';
import { isAuth, disableFutureDate, replaceNullAndSpace } from '@helpers';

import { ExportToExcel } from '@sharedComponent/export-to-excel';
import '@sharedComponent/round-bar';

const dateFormat = 'MM/DD/YYYY';
const { RangePicker } = DatePicker;
const { Option } = Select;

const option = {
	tooltips: {
		yPadding: 15,
		xPadding: 15,
		bodyFontFamily: 'Gilroy-Semibold',
	},
	legend: {
		display: true,
		position: 'bottom',
		// align: 'start',
		fullWidth: true,

		labels: {
			padding: 30,
			usePointStyle: true,
			fontFamily: 'Gilroy-Semibold',
			fontSize: 10,
			fontColor: '#253D5C',
		},
	},
	maintainAspectRatio: false,
	responsive: true,
	cornerRadius: 15,

	scales: {
		xAxes: [
			{
				// barThickness: 16,
				beginAtZero: true,
				ticks: {
					padding: 5,
					labelOffset: 5,
					beginAtZero: true,
					fontFamily: 'Gilroy-Semibold',
					fontSize: 12,
				},
				scaleLabel: {
					display: false,
				},
				gridLines: { display: false, tickMarkLength: 2 },
			},
		],
		yAxes: [
			{
				gridLines: {
					borderDash: [8, 4],
				},
				ticks: {
					fontFamily: 'Gilroy-Semibold',
					fontSize: 12,
					beginAtZero: true,
					stacked: true,
					// steps: 10,
					// stepValue: 5,
					// max: 1000,
				},
			},
		],
	},
};

export const PerformanceReportPresentational = ({
	state,
	performanceData,
	handlePerformance,
	handleChange,
	handleChartDataType,
	changeChartType,
	setCurrentData,
	dataRangeChanged,
	// data,
	userDetails,
	datefilter,
	handleCampainList,
	campaignList,
	loaders,
}) => {
	const { chartLoading, tableLoading } = loaders;
	const columns = [
		isAuth(performanceReportColoums, userDetails && userDetails.accessLevel)
			? {
					title: 'COMPANY',
					dataIndex: 'company',
					key: 'company',
					sorter: (a, b) => a.company.localeCompare(b.company),
					render: (value, row) => replaceNullAndSpace(value),
			  }
			: {},
		isAuth(performanceReportColoums, userDetails && userDetails.accessLevel)
			? {
					title: 'Affiliate',
					dataIndex: 'affiliateName',
					key: 'affiliateName',
					sorter: (a, b) => a.affiliateName.localeCompare(b.affiliateName),
					render: (value, row) => replaceNullAndSpace(value),
			  }
			: {},
		{
			title: 'Date',
			dataIndex: 'dateCreated',
			key: 'dateCreated',
			sorter: (a, b) => a.dateCreated.localeCompare(b.dateCreated),
			render: (value, row, index) => {
				return replaceNullAndSpace(moment(value).format(dateFormat));
			},
		},
		{
			title: 'Campaign',
			dataIndex: 'categoryName',
			key: 'categoryName',
			sorter: (a, b) => a.categoryName.localeCompare(b.categoryName),
			render: (value, row) => replaceNullAndSpace(value),
		},
		{
			title: 'CLICKS',
			dataIndex: 'clicks',
			key: 'clicks',
			sorter: (a, b) => {
				return a.clicks - b.clicks;
			},
			render: (value, row) => replaceNullAndSpace(value),
		},
		{
			title: 'CPC COMMISSION',
			dataIndex: 'cpcCommission',
			key: 'cpcCommission',
			sorter: (a, b) => a.cpcCommission - b.cpcCommission,
			render: (value, row) => replaceNullAndSpace(value),
		},
		{
			title: 'PARTIAL',
			dataIndex: 'partials',
			key: 'partials',
			sorter: (a, b) => a.partials - b.partials,
			render: (value, row) => replaceNullAndSpace(value),
		},
		{
			title: 'PARTIAL %',
			dataIndex: 'partialPerc',
			key: 'partialPerc',
			sorter: (a, b) => a.partialPerc.localeCompare(b.partialPerc),
			render: (value, row) => replaceNullAndSpace(value),
		},
		{
			title: 'DECLINES',
			dataIndex: 'declines',
			key: 'declines',
			sorter: (a, b) => a.declines - b.declines,
			render: (value, row) => replaceNullAndSpace(value),
		},
		{
			title: 'DECLINES %',
			dataIndex: 'declinePerc',
			key: 'declinePerc',
			sorter: (a, b) => a.declinePerc.localeCompare(b.declinePerc),
			render: (value, row) => replaceNullAndSpace(value),
		},
		{
			title: 'SALES',
			dataIndex: 'sales',
			key: 'sales',
			sorter: (a, b) => a.sales - b.sales,
			render: (value, row) => replaceNullAndSpace(value),
		},
		{
			title: 'CONVERSION',
			dataIndex: 'conversions',
			key: 'conversions',
			sorter: (a, b) => a.conversions - b.conversions,
			render: (value, row) => replaceNullAndSpace(value),
		},
		{
			title: 'CONVERSION %',
			dataIndex: 'conversionPerc',
			key: 'conversionPerc',
			sorter: (a, b) => a.conversionPerc.localeCompare(b.conversionPerc),
			render: (value, row) => replaceNullAndSpace(value),
		},
		{
			title: 'STEP 1 COMMISSION',
			dataIndex: 'step1Commission',
			key: 'step1Commission',
			sorter: (a, b) => a.step1Commission.localeCompare(b.step1Commission),
			render: (value, row) => replaceNullAndSpace(value),
		},
		{
			title: 'SALES REVENUE',
			dataIndex: 'saleTotal',
			key: 'saleTotal',
			sorter: (a, b) => a.saleTotal.localeCompare(b.saleTotal),
			render: (value, row) => replaceNullAndSpace(value),
		},
		{
			title: 'UPSELLS',
			dataIndex: 'upsells',
			key: 'upsells',
			sorter: (a, b) => a.upsells - b.upsells,
			render: (value, row) => replaceNullAndSpace(value),
		},
		{
			title: 'UPSELLS %',
			dataIndex: 'upsellPerc',
			key: 'upsellPerc',
			sorter: (a, b) => a.upsellPerc.localeCompare(b.upsellPerc),
			render: (value, row) => replaceNullAndSpace(value),
		},
		{
			title: 'UPSELL REVENUE',
			dataIndex: 'upsellTotal',
			key: 'upsellTotal',
			sorter: (a, b) => a.upsellTotal.localeCompare(b.upsellTotal),
			render: (value, row) => replaceNullAndSpace(value),
		},
		{
			title: 'STEP 2 COMMISSION',
			dataIndex: 'step2Commission',
			key: 'step2Commission',
			sorter: (a, b) => a.step2Commission.localeCompare(b.step2Commission),
			render: (value, row) => replaceNullAndSpace(value),
		},
		{
			title: 'Net',
			dataIndex: 'netTotal',
			key: 'netTotal',
			sorter: (a, b) => a.netTotal.localeCompare(b.netTotal),
			render: (value, row) => replaceNullAndSpace(value),
		},
		{
			title: 'Avg Ticket',
			dataIndex: 'avgTicket',
			key: 'avgTicket',
			sorter: (a, b) => a.avgTicket.localCompare(b.avgTicket),
			render: (value, row, index) => {
				return parseFloat(value).toFixed(2);
			},
		},
	];

	var data = {
		option: option,
		labels: performanceData.labels || [],
		datasets: performanceData.datasetsTemp || [],
	};

	const locale = {
		emptyText: 'No Performance Report data to display',
	};

	return (
		<>
			<Header title={'Dashboard / Performance Report'} />
			<Row className="performance_report_container">
				<Row className="chart_area">
					<Col xl={24} style={{ width: '100%' }}>
						<Col className="chart_container">
							<Row className="chart_header">
								<Col xl={8} lg={8} md={8} sm={24} xs={24}>
									<SpanComponent className="title">
										<Select
											defaultValue={performanceData.chartDataType}
											onChange={(e) => {
												handleChartDataType('chartDataType', e);
											}}
											style={{ width: 120 }}>
											<Option value="Clicks">Clicks</Option>
											<Option value="Sales">Sales</Option>
											<Option value="Partial">Partial</Option>
											<Option value="Rebills">Rebills</Option>
											<Option value="Declines">Declines</Option>
										</Select>
									</SpanComponent>
									{'    '}
									<SpanComponent className="title">
										<Select
											placeholder={'Campaign'}
											mode="multiple"
											value={performanceData.campaignValue}
											maxTagCount={1}
											allowClear={true}
											onChange={handleCampainList}
											style={{ minWidth: 120 }}>
											{/* <Option value="Clicks">Campaign 1</Option>
											<Option value="Sales">Campaign 2</Option>
											<Option value="Partial">Campaign 3</Option> */}
											<Option value="ALL">All</Option>
											{performanceData.campaignList.map((value) => {
												return <Option value={value}>{value}</Option>;
											})}
										</Select>
									</SpanComponent>
								</Col>
								<Col xl={16} lg={16} md={16} sm={24} xs={24} className="chart_header_right_area">
									<Row>
										<Col className="actions">
											<Button
												onClick={() => {
													changeChartType('chartType', 'bar');
												}}
												style={{
													height: '32px',
													padding: '0px',
												}}
												type={state.chartType === 'bar' ? 'primary' : null}
												icon={<BarChartOutlined />}
												size={'middle'}
											/>
										</Col>
										<Col className="actions">
											<Button
												style={{
													height: '32px',
													padding: '0px',
												}}
												onClick={() => {
													changeChartType('chartType', 'line');
												}}
												type={state.chartType === 'line' ? 'primary' : null}
												icon={<LineChartOutlined />}
												size={'middle'}
											/>
										</Col>
										<Col className="actions">
											<RangePicker
												disabled={performanceData.dateFilter !== 'CUSTOM'}
												disabledDate={disableFutureDate}
												// open={performanceData.isOpenDatePicker}
												value={[moment(performanceData.startDate, dateFormat), moment(performanceData.endDate, dateFormat)]}
												format={dateFormat}
												onCalendarChange={(e, value) => {
													handlePerformance('campaignValue', []);
													dataRangeChanged(value);
												}}
											/>
										</Col>
										<Col className="actions">
											<Select
												value={performanceData.dateFilter}
												// defaultValue={performanceData.dateFilter}
												onSelect={(value) => {
													datefilter(value);
												}}
												style={{ width: 120 }}>
												<Option value="LAST">Last 7 days</Option>
												<Option value="YEST">Yesterday</Option>
												<Option value="THIS_WEEK">This week</Option>
												<Option value="THIS_MONTH">This month</Option>
												<Option value="CUSTOM">Custom</Option>
											</Select>
										</Col>
									</Row>
								</Col>
							</Row>
							<Col xl={24} style={{ overflowX: 'scroll' }}>
								{chartLoading ? (
									<Col style={{ minHeight: 50, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
										<AppLoader width="5%" height="10%" />
									</Col>
								) : (
									<Bar data={data} type={state.chartType} width={100} height={400} options={data.option} />
								)}
							</Col>
						</Col>
					</Col>
				</Row>
				<Row className="chart_area">
					<Col xl={24} style={{ width: '100%' }}>
						<Col className="chart_container" style={{ width: '100%' }}>
							<Row className="chart_header">
								<Col xl={8}>
									<SpanComponent className="title">Performance Report</SpanComponent>
								</Col>
								<Col xl={16} className="chart_header_right_area">
									<Row>
										<Col
											className={state.zoomicontype === 'activeout' ? 'zoomicon active' : 'zoomicon'}
											onClick={() => handleChange('zoomicontype', 'activeout')}>
											<FullscreenOutlined />
										</Col>

										<Col
											className={state.zoomicontype === 'activeexit' ? 'zoomicon active' : 'zoomicon'}
											onClick={() => handleChange('zoomicontype', 'activeexit')}>
											<FullscreenExitOutlined />
										</Col>

										<Col className="export_area" style={{ float: 'right' }}>
											<Button type="primary" className="excel_download" icon={<DownloadOutlined />} size={'middle'}>
												Export to EXCEL
												<PerformanceExcelDownload
													style={{ visibility: 'hidden' }}
													dataSet={state.dataSource}
													userDetails={userDetails}
													filename={`${moment(new Date()).format(dateFormat)}_Performance_Report`}
												/>
											</Button>
										</Col>
									</Row>
								</Col>
								<Col
									xl={24}
									className="aff_usage_table_container"
									style={{ zoom: state.zoomicontype === 'activeexit' ? '60%' : '100%' }}>
									{tableLoading ? (
										<AppLoader width={'5%'} height={'83%'} />
									) : (
										<Table
											locale={locale}
											pagination={{
												position: ['bottomCenter', 'bottomCenter'],
												defaultPageSize: 5,
												showSizeChanger: true,
												pageSizeOptions: ['5', '10'],
											}}
											dataSource={state.dataSource}
											columns={columns}
										/>
									)}
								</Col>
							</Row>
						</Col>
					</Col>
				</Row>
			</Row>
		</>
	);
};
